import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getCountryVatRateListFailure,
	getCountryVatRateListRequest,
	getCountryVatRateListSuccess,
	syncCountryVatRatesFailure,
	syncCountryVatRatesRequest,
	syncCountryVatRatesSuccess,
	uploadCountryVatRatesFailure,
	uploadCountryVatRatesRequest,
	uploadCountryVatRatesSuccess,
	getNonEuCountryVatRateListFailure,
	getNonEuCountryVatRateListRequest,
	getNonEuCountryVatRateListSuccess,
	getSupportedNonEuCountryListFailure,
	getSupportedNonEuCountryListRequest,
	getSupportedNonEuCountryListSuccess,
	getHs6pCodeListFailure,
	getHs6pCodeListRequest,
	getHs6pCodeListSuccess,
	getCustomsDutyRateListFailure,
	getCustomsDutyRateListRequest,
	getCustomsDutyRateListSuccess,
	getSpecialTerritoryListFailure,
	getSpecialTerritoryListRequest,
	getSpecialTerritoryListSuccess,
	createSpecialTerritoryRequest,
	updateSpecialTerritoryRequest,
	saveSpecialTerritorySuccess,
	saveSpecialTerritoryFailure,
	getSpecialTerritoryPostalCodeListFailure,
	getSpecialTerritoryPostalCodeListRequest,
	getSpecialTerritoryPostalCodeListSuccess,
	createSpecialTerritoryPostalCodeRequest,
	updateSpecialTerritoryPostalCodeRequest,
	saveSpecialTerritoryPostalCodeSuccess,
	saveSpecialTerritoryPostalCodeFailure,
} from './reducer';
import {
	getCountryVatRateLastListParams,
	getSpecialTerritoryLastListParams,
	getSpecialTerritoryPostalCodeLastListParams,
} from './selectors';
import {
	TCountryVatRateListRequest,
	TCountryVatRateListResponse,
	TCreateOrUpdateSpecialTerritoryPostalCodeRequest,
	TCreateOrUpdateSpecialTerritoryRequest,
	TCustomsDutyRateListRequest,
	TCustomsDutyRateListResponse,
	TSpecialTerritoryListRequest,
	TSpecialTerritoryListResponse,
	TSpecialTerritoryPostalCodeListRequest,
	TSpecialTerritoryPostalCodeListResponse,
	TUpdateSpecialTerritoryPostalCodeRequest,
	TUpdateSpecialTerritoryRequest,
	THs6pCodeListRequest,
	THs6pCodeListResponse,
	TSupportedNonEuCountry,
	TUploadCountryVatRatesRequest,
} from './types';

const DEFAULT_LIST_PARAMS: TCountryVatRateListRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

function* getCountryVatRateListWorker({ payload }: PayloadAction<TCountryVatRateListRequest>) {
	try {
		const response: TCountryVatRateListResponse = yield call(
			api.taxDuties.getCountryVatRateList,
			payload,
		);
		yield put(getCountryVatRateListSuccess(response));
	} catch (error) {
		yield put(getCountryVatRateListFailure());
		notifyError(error, 'Failed to load EU VAT rates');
	}
}

function* refetchCountryVatRateList() {
	const lastParams: TCountryVatRateListRequest | null = yield select(
		getCountryVatRateLastListParams,
	);
	const response: TCountryVatRateListResponse = yield call(
		api.taxDuties.getCountryVatRateList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getCountryVatRateListSuccess(response));
}

function* syncCountryVatRatesWorker() {
	try {
		yield call(api.taxDuties.syncCountryVatRates);
		yield call(refetchCountryVatRateList);
		yield put(syncCountryVatRatesSuccess());
		notificationContainer('EU VAT rates were successfully synced', 'success');
	} catch (error) {
		yield put(syncCountryVatRatesFailure());
		notifyError(error, 'Failed to sync EU VAT rates');
	}
}

function* uploadCountryVatRatesWorker({ payload }: PayloadAction<TUploadCountryVatRatesRequest>) {
	try {
		yield call(api.taxDuties.uploadCountryVatRates, payload);
		yield call(refetchCountryVatRateList);
		yield put(uploadCountryVatRatesSuccess());
		notificationContainer('EU VAT rates file was successfully uploaded', 'success');
	} catch (error) {
		yield put(uploadCountryVatRatesFailure());
		notifyError(error, 'Failed to upload the EU VAT rates file');
	}
}

function* getNonEuCountryVatRateListWorker({ payload }: PayloadAction<TCountryVatRateListRequest>) {
	try {
		const response: TCountryVatRateListResponse = yield call(
			api.taxDuties.getNonEuCountryVatRateList,
			payload,
		);
		yield put(getNonEuCountryVatRateListSuccess(response));
	} catch (error) {
		yield put(getNonEuCountryVatRateListFailure());
		notifyError(error, 'Failed to load non-EU VAT rates');
	}
}

function* getSupportedNonEuCountryListWorker() {
	try {
		const response: TSupportedNonEuCountry[] = yield call(
			api.taxDuties.getSupportedNonEuCountryList,
		);
		yield put(getSupportedNonEuCountryListSuccess(response ?? []));
	} catch (error) {
		yield put(getSupportedNonEuCountryListFailure());
		notifyError(error, 'Failed to load the non-EU country list');
	}
}

function* getHs6pCodeListWorker({ payload }: PayloadAction<THs6pCodeListRequest>) {
	try {
		const response: THs6pCodeListResponse = yield call(api.taxDuties.getHs6pCodeList, payload);
		yield put(getHs6pCodeListSuccess(response));
	} catch (error) {
		yield put(getHs6pCodeListFailure());
		notifyError(error, 'Failed to load HS6+ codes');
	}
}

function* getCustomsDutyRateListWorker({ payload }: PayloadAction<TCustomsDutyRateListRequest>) {
	try {
		const response: TCustomsDutyRateListResponse = yield call(
			api.taxDuties.getCustomsDutyRateList,
			payload,
		);
		yield put(getCustomsDutyRateListSuccess(response));
	} catch (error) {
		yield put(getCustomsDutyRateListFailure());
		notifyError(error, 'Failed to load EU customs duty rates');
	}
}

function* getSpecialTerritoryListWorker({ payload }: PayloadAction<TSpecialTerritoryListRequest>) {
	try {
		const response: TSpecialTerritoryListResponse = yield call(
			api.taxDuties.getSpecialTerritoryList,
			payload,
		);
		yield put(getSpecialTerritoryListSuccess(response));
	} catch (error) {
		yield put(getSpecialTerritoryListFailure());
		notifyError(error, 'Failed to load EU special VAT territories');
	}
}

function* refetchSpecialTerritoryList() {
	const lastParams: TSpecialTerritoryListRequest | null = yield select(
		getSpecialTerritoryLastListParams,
	);
	const response: TSpecialTerritoryListResponse = yield call(
		api.taxDuties.getSpecialTerritoryList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getSpecialTerritoryListSuccess(response));
}

function* createSpecialTerritoryWorker({
	payload,
}: PayloadAction<TCreateOrUpdateSpecialTerritoryRequest>) {
	try {
		yield call(api.taxDuties.createSpecialTerritory, payload);
		yield call(refetchSpecialTerritoryList);
		yield put(saveSpecialTerritorySuccess());
		notificationContainer('Special territory was successfully created', 'success');
	} catch (error) {
		yield put(saveSpecialTerritoryFailure());
		notifyError(error, 'Failed to create the special territory');
	}
}

function* updateSpecialTerritoryWorker({ payload }: PayloadAction<TUpdateSpecialTerritoryRequest>) {
	try {
		yield call(api.taxDuties.updateSpecialTerritory, payload);
		yield call(refetchSpecialTerritoryList);
		yield put(saveSpecialTerritorySuccess());
		notificationContainer('Special territory was successfully updated', 'success');
	} catch (error) {
		yield put(saveSpecialTerritoryFailure());
		notifyError(error, 'Failed to update the special territory');
	}
}

function* getSpecialTerritoryPostalCodeListWorker({
	payload,
}: PayloadAction<TSpecialTerritoryPostalCodeListRequest>) {
	try {
		const response: TSpecialTerritoryPostalCodeListResponse = yield call(
			api.taxDuties.getSpecialTerritoryPostalCodeList,
			payload,
		);
		yield put(getSpecialTerritoryPostalCodeListSuccess(response));
	} catch (error) {
		yield put(getSpecialTerritoryPostalCodeListFailure());
		notifyError(error, 'Failed to load the postal codes');
	}
}

function* refetchSpecialTerritoryPostalCodeList() {
	const lastParams: TSpecialTerritoryPostalCodeListRequest | null = yield select(
		getSpecialTerritoryPostalCodeLastListParams,
	);
	// Without the params of the open territory a refetch would pull every postal code there is.
	if (!lastParams) return;
	const response: TSpecialTerritoryPostalCodeListResponse = yield call(
		api.taxDuties.getSpecialTerritoryPostalCodeList,
		lastParams,
	);
	yield put(getSpecialTerritoryPostalCodeListSuccess(response));
}

function* createSpecialTerritoryPostalCodeWorker({
	payload,
}: PayloadAction<TCreateOrUpdateSpecialTerritoryPostalCodeRequest>) {
	try {
		yield call(api.taxDuties.createSpecialTerritoryPostalCode, payload);
		yield call(refetchSpecialTerritoryPostalCodeList);
		yield put(saveSpecialTerritoryPostalCodeSuccess());
		notificationContainer('Postal code was successfully added', 'success');
	} catch (error) {
		yield put(saveSpecialTerritoryPostalCodeFailure());
		notifyError(error, 'Failed to add the postal code');
	}
}

function* updateSpecialTerritoryPostalCodeWorker({
	payload,
}: PayloadAction<TUpdateSpecialTerritoryPostalCodeRequest>) {
	try {
		yield call(api.taxDuties.updateSpecialTerritoryPostalCode, payload);
		yield call(refetchSpecialTerritoryPostalCodeList);
		yield put(saveSpecialTerritoryPostalCodeSuccess());
		notificationContainer('Postal code was successfully updated', 'success');
	} catch (error) {
		yield put(saveSpecialTerritoryPostalCodeFailure());
		notifyError(error, 'Failed to update the postal code');
	}
}

export function* taxDutiesSaga() {
	yield takeLatest(getCountryVatRateListRequest.type, getCountryVatRateListWorker);
	yield takeLatest(syncCountryVatRatesRequest.type, syncCountryVatRatesWorker);
	yield takeLatest(uploadCountryVatRatesRequest.type, uploadCountryVatRatesWorker);
	yield takeLatest(getNonEuCountryVatRateListRequest.type, getNonEuCountryVatRateListWorker);
	yield takeLatest(getSupportedNonEuCountryListRequest.type, getSupportedNonEuCountryListWorker);
	yield takeLatest(getHs6pCodeListRequest.type, getHs6pCodeListWorker);
	yield takeLatest(getCustomsDutyRateListRequest.type, getCustomsDutyRateListWorker);
	yield takeLatest(getSpecialTerritoryListRequest.type, getSpecialTerritoryListWorker);
	yield takeLatest(createSpecialTerritoryRequest.type, createSpecialTerritoryWorker);
	yield takeLatest(updateSpecialTerritoryRequest.type, updateSpecialTerritoryWorker);
	yield takeLatest(
		getSpecialTerritoryPostalCodeListRequest.type,
		getSpecialTerritoryPostalCodeListWorker,
	);
	yield takeLatest(
		createSpecialTerritoryPostalCodeRequest.type,
		createSpecialTerritoryPostalCodeWorker,
	);
	yield takeLatest(
		updateSpecialTerritoryPostalCodeRequest.type,
		updateSpecialTerritoryPostalCodeWorker,
	);
}
